import { AxiosError } from "axios";

import { getUserInternalAccessToken } from "@/api/access-token-backoffice";
import { buildPaginationQuery } from "@/helpers/request-helpers";
import { IWorkflowType } from "@/interfaces/workflow-type.interface";
import { apiBackofficeT2b } from "@/lib/axios";
import { PaginatedResponse, RequestPagination } from "@/types/requests.types";

const baseUrl = "v1/workflowtype";

function normalizeItem(raw: Record<string, unknown>): IWorkflowType | null {
  const idRaw = raw.id ?? raw.Id;
  const id = typeof idRaw === "number" ? idRaw : Number(idRaw);
  if (!Number.isFinite(id)) return null;

  const name = String(raw.name ?? raw.Name ?? "");
  const description = String(raw.description ?? raw.Description ?? "");
  const rowDateRaw = raw.rowDate ?? raw.RowDate;
  const rowDate =
    rowDateRaw instanceof Date
      ? rowDateRaw
      : typeof rowDateRaw === "string" || typeof rowDateRaw === "number"
        ? new Date(rowDateRaw)
        : new Date();

  return { id, name, description, rowDate };
}

export async function getWorkflowTypes(
  pagination: RequestPagination
): Promise<PaginatedResponse<IWorkflowType>> {
  try {
    const { accessToken } = await getUserInternalAccessToken();
    const paginationQuery = buildPaginationQuery(pagination);
    const config = { headers: { Authorization: `Bearer ${accessToken}` } };

    const response = await apiBackofficeT2b.get(`${baseUrl}?${paginationQuery}`, config);
    const data = response.data as Record<string, unknown> & {
      items?: Record<string, unknown>[];
      Items?: Record<string, unknown>[];
    };

    const rawItems = data.items ?? data.Items ?? [];
    const items = rawItems
      .map((row) => normalizeItem(row as Record<string, unknown>))
      .filter((row): row is IWorkflowType => row !== null);

    return {
      items,
      totalCount: Number(data.totalCount ?? data.TotalCount ?? items.length),
      pageNumber: Number(data.pageNumber ?? data.PageNumber ?? pagination.pageNumber),
      pageSize: Number(data.pageSize ?? data.PageSize ?? pagination.pageSize),
      hasNextPage: Boolean(data.hasNextPage ?? data.HasNextPage ?? false),
      hasPreviousPage: Boolean(data.hasPreviousPage ?? data.HasPreviousPage ?? false),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || "Failed to fetch workflow types");
    }
    throw new Error("Failed to fetch workflow types");
  }
}
